import API from "./UserPreferencesService";
import { getStoredAuthToken, subscribeAuthTokenChanges } from "./authTokenStorage";

const emptySnapshot = { view: null, saveStatus: "idle", revision: 0 };
const entries = new Map();
const listeners = new Map();
let sessionToken;

function notify(accountId) {
  const subscribers = listeners.get(accountId);
  if (subscribers) subscribers.forEach(listener => listener());
}

function invalidateSession() {
  sessionToken = getStoredAuthToken();
  entries.clear();
  listeners.forEach((value, accountId) => notify(accountId));
}

subscribeAuthTokenChanges(invalidateSession);

function syncSession() {
  if (sessionToken !== getStoredAuthToken()) invalidateSession();
}

function getSnapshot(accountId) {
  syncSession();
  return entries.get(accountId) || emptySnapshot;
}

async function save(accountId, pendingEntry) {
  const entry = pendingEntry;
  const { view, revision } = entry;
  entry.inFlight = true;
  entry.saveStatus = "saving";
  notify(accountId);
  try {
    await API.saveCalendarView(view);
    syncSession();
    if (entries.get(accountId) !== entry) return;
    entry.inFlight = false;
    if (entry.view !== view) {
      save(accountId, entry);
      return;
    }
    entry.saveStatus = "idle";
  } catch (error) {
    syncSession();
    if (entries.get(accountId) !== entry) return;
    entry.inFlight = false;
    // A new deliberate choice during the failed request should still be tried.
    if (entry.revision !== revision) {
      save(accountId, entry);
      return;
    }
    entry.saveStatus = "error";
  }
  notify(accountId);
}

function select(accountId, view) {
  syncSession();
  if (!accountId || !sessionToken) return;
  const entry = entries.get(accountId) || { ...emptySnapshot };
  entry.view = view;
  entry.revision += 1;
  entries.set(accountId, entry);
  if (!entry.inFlight) save(accountId, entry);
  else notify(accountId);
}

function retry(accountId) {
  syncSession();
  const entry = entries.get(accountId);
  if (entry && entry.saveStatus === "error") save(accountId, entry);
}

function subscribe(accountId, listener) {
  if (!listeners.has(accountId)) listeners.set(accountId, new Set());
  const subscribers = listeners.get(accountId);
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
    if (!subscribers.size) listeners.delete(accountId);
  };
}

export default { getSnapshot, select, retry, subscribe };
